import type {
  EdgeData,
  EdgeId,
  PathLookupEdgeData,
  TopologyEdgeData,
  UnpositionedEdgeData,
  UnpositionedPathLookupEdgeData,
  UnpositionedTopologyEdgeData,
} from '../edges/index';
import type {
  NodeData,
  PathLookupNodeData,
  TopologyNodeData,
  UnpositionedNode,
  UnpositionedPathLookupNode,
  UnpositionedTopologyNode,
} from '../nodes/index';
import type { NodeId } from '../nodes/types';

export interface GraphData<
  N = unknown,
  E = unknown,
  NId extends string = string,
  EId extends string = string,
> {
  nodes: Map<NId, N>;
  edges: Map<EId, { source: NId; target: NId | null; data: E }>;
  adjacencyList: Map<NId, Set<EId>>;
}

type Edge<EId, NId, E> = { id: EId; source: NId; target: NId | null; data: E };

/**
 * Directed Pseudograph Implementation
 */
export class Graph<
  N = unknown,
  E = unknown,
  NId extends string = string,
  EId extends string = string,
> {
  private _adjacencyList: Map<NId, Set<EId>>;
  private _edges: Map<EId, Omit<Edge<EId, NId, E>, 'id'>>;
  private _nodes: Map<NId, N>; // WARNING: This only contains nodes that had custom data

  constructor() {
    this._adjacencyList = new Map();
    this._edges = new Map();
    this._nodes = new Map();
  }

  /**
   * Get number of nodes
   */
  get size(): number {
    return this._adjacencyList.size;
  }

  /**
   * Check whether graph is empty or not
   */
  get isEmpty(): boolean {
    return this.size === 0;
  }

  /**
   * Alias for `size` to maintain naming consistency with `edgeCount`
   */
  get nodeCount(): number {
    return this.size;
  }

  /**
   * Get number of edges
   */
  get edgeCount(): number {
    return this._edges.size;
  }

  public static fromData<Nodes = unknown, Edges = unknown>(
    serializedData: GraphData<Nodes, Edges>,
  ): Graph<Nodes, Edges> {
    const graph = new Graph<Nodes, Edges>();
    graph.restoreFromData(serializedData);
    return graph;
  }

  public restoreFromData({
    nodes,
    edges,
    adjacencyList,
  }: GraphData<N, E, NId, EId>): void {
    this._nodes = nodes;
    this._edges = edges;
    this._adjacencyList = adjacencyList;
  }

  public getData(): GraphData<N, E> {
    return {
      nodes: this._nodes,
      edges: this._edges,
      adjacencyList: this._adjacencyList,
    };
  }

  public node(id: NId): { id: NId; data: N } | undefined {
    if (!this._adjacencyList.has(id)) {
      return undefined;
    }
    return { id, data: this._nodes.get(id)! };
  }

  public *nodes(): IterableIterator<{ id: NId; data: N }> {
    for (const id of this._adjacencyList.keys()) {
      const node = this.node(id);
      if (node) {
        yield node;
      }
    }
  }

  public addNode(id: NId, node?: N): this {
    if (this._adjacencyList.has(id)) {
      throw new Error(`Node ${JSON.stringify(id)} already exists`);
    }

    if (node) {
      // There is no need to store the `node` data since there are none
      this._nodes.set(id, node);
    }
    this._adjacencyList.set(id, new Set());

    return this;
  }

  public removeNode(nodeId: NId): this {
    // Do not do anything when the node doesn't exist
    if (!this._adjacencyList.has(nodeId)) {
      return this;
    }

    // Remove edges
    for (const [edgeId, edge] of this._edges.entries()) {
      if (edge.source === nodeId || edge.target === nodeId) {
        this.removeEdge(edgeId);
      }
    }

    this._nodes.delete(nodeId);
    this._adjacencyList.delete(nodeId);

    return this;
  }

  /**
   * Returns true if the node represented by the passed node ID only has a single neighbor.
   * This is supported only for undirected graphs, i.e. `source` and `target` are treated the same.
   */
  public hasSingleNeighbor(nodeId: NId): boolean {
    const neighbors = new Set(
      Array.from(this.edges(nodeId)).map((edge) =>
        edge.source === nodeId ? edge.target : edge.source,
      ),
    );

    return neighbors.size === 1;
  }

  public edge(id: EId): Edge<EId, NId, E> | undefined {
    if (!this._edges.has(id)) {
      return undefined;
    }
    return { id, ...this._edges.get(id)! };
  }

  public *edges(nodeId?: NId): IterableIterator<Edge<EId, NId, E>> {
    if (!nodeId) {
      for (const id of this._edges.keys()) {
        yield this.edge(id)!;
      }
      return;
    }
    for (const [id, { source, target }] of this._edges.entries()) {
      if (source === nodeId || target === nodeId) {
        yield this.edge(id)!;
      }
    }
  }

  public hasEdge(nodeId?: NId): boolean {
    if (!nodeId) {
      // Check if there is any edge in the graph
      return this._edges.size > 0;
    }
    if (!this._adjacencyList.has(nodeId)) {
      throw new Error(`Node "${nodeId}" does not exist`);
    }
    if (this._adjacencyList.get(nodeId)!.size > 0) {
      // There is at least one outgoing edge from `nodeId`
      return true;
    }
    for (const [, { target }] of this._edges) {
      if (target === nodeId) {
        return true;
      }
    }
    return false;
  }

  public *edgesFrom(nodeId: NId): IterableIterator<Edge<EId, NId, E>> {
    for (const id of this._adjacencyList.get(nodeId) ?? []) {
      const edge = this.edge(id);
      if (edge) {
        yield edge;
      }
    }
  }

  public *edgesBetween(
    node1: NId,
    node2: NId,
  ): IterableIterator<Edge<EId, NId, E>> {
    for (const edgeId of this._adjacencyList.get(node1) ?? []) {
      const edge = this.edge(edgeId);
      if (edge?.target === node2) {
        yield edge;
      }
    }
    for (const edgeId of this._adjacencyList.get(node2) ?? []) {
      const edge = this.edge(edgeId);
      if (edge?.target === node1) {
        yield edge;
      }
    }
  }

  public hasEdgeBetween(node1: NId, node2: NId): boolean {
    if (!this._adjacencyList.has(node1)) {
      throw new Error(`Node "${node1}" does not exist`);
    }
    if (!this._adjacencyList.has(node2)) {
      throw new Error(`Node "${node2}" does not exist`);
    }

    for (const edgeId of this._adjacencyList.get(node1) ?? []) {
      const edge = this.edge(edgeId);
      if (edge?.target === node2) {
        return true;
      }
    }

    for (const edgeId of this._adjacencyList.get(node2) ?? []) {
      const edge = this.edge(edgeId);
      if (edge?.target === node1) {
        return true;
      }
    }
    return false;
  }

  /**
   * Adds the edge to the graph
   *
   * The last parameter to be optional only when E allows undefined.
   * I don't know if there is a way to declare this directly, but
   * the hacky solution with conditional type of id works as needed -
   * it makes the first overload signature usable only when E allows undefined.
   */
  public addEdge(
    id: E extends undefined ? EId : never,
    source: NId,
    target: NId | null,
    edge?: E,
  ): this;
  public addEdge(id: EId, source: NId, target: NId | null, edge: E): this;
  public addEdge(id: EId, source: NId, target: NId | null, edge?: E): this {
    const logname = `Edge "${id}" ("${source}" -> "${target}")`;
    if (this._edges.has(id)) {
      throw new Error(`${logname} is already present`);
    }
    if (!this._adjacencyList.has(source)) {
      throw new Error(`${logname} can't be added! Source node is not present`);
    }
    if (target && !this._adjacencyList.has(target)) {
      throw new Error(`${logname} can't be added! Target node is not present`);
    }
    this._edges.set(id, { source, target, data: edge! });
    this._adjacencyList.get(source)!.add(id);
    return this;
  }

  /**
   * Removes the edge from the graph
   */
  public removeEdge(id: EId): this {
    // Do not do anything when the node doesn't exist
    if (!this._edges.has(id)) {
      return this;
    }

    const { source } = this._edges.get(id)!;
    this._edges.delete(id);
    this._adjacencyList.get(source)?.delete(id);

    return this;
  }

  // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: legacy code
  public *bfs(
    start?: NId,
    visitedAlready?: Set<NId>,
    directed = true,
  ): IterableIterator<{ id: NId; data: N }> {
    if (start && !this._adjacencyList.has(start)) {
      throw new RangeError(
        'The provided `start` node is not present in the graph',
      );
    }

    const queue: NId[] = [];
    const visited: Set<NId> = visitedAlready || new Set();
    const rootId = start || this._adjacencyList.keys().next().value;

    if (!rootId) {
      return;
    }

    // initialize with root
    queue.push(rootId);
    visited.add(rootId);

    const node = this.node(rootId);
    if (node) {
      yield node;
    }

    // Define function depending on `directed` property. It is defined here to get rid of one comparison per every node.
    const getEdges = directed
      ? (nodeId: NId) => this.edgesFrom(nodeId)
      : (nodeId: NId) => this.edges(nodeId);
    const getNeighborId = directed
      ? (_source: NId, target: NId) => target
      : (source: NId, target: NId, nodeId: NId) =>
          source === nodeId ? target : source;

    while (queue.length) {
      const currentNode = queue.shift()!;

      for (const { source, target } of getEdges(currentNode)) {
        if (target) {
          const neighborId = getNeighborId(source, target, currentNode);

          if (visited.has(neighborId)) {
            continue;
          }

          visited.add(neighborId);
          queue.push(neighborId);

          const neighbor = this.node(neighborId);
          if (neighbor) {
            yield neighbor;
          }
        }
      }
    }
  }

  /**
   * Returns components of an undirected version of the graph
   */
  public getComponents(): { id: NId; data: N }[][] {
    const visited: Set<NId> = new Set();
    const unvisited: Set<NId> = new Set([...this._adjacencyList.keys()]);
    const components: { id: NId; data: N }[][] = [];

    while (unvisited.size) {
      components.push([]);
      for (const node of this.bfs(
        unvisited.values().next().value,
        visited,
        false,
      )) {
        components[components.length - 1]?.push(node);
        unvisited.delete(node.id);
      }
    }

    return components;
  }
}

export type PositionedGraph = Graph<NodeData, EdgeData, NodeId, EdgeId>;
export type UnpositionedGraph = Graph<
  UnpositionedNode,
  UnpositionedEdgeData<EdgeData>,
  NodeId,
  EdgeId
>;
export type UnpositionedGraphData = GraphData<
  UnpositionedNode,
  UnpositionedEdgeData<EdgeData>,
  NodeId,
  EdgeId
>;

export type PositionedTopologyGraph = Graph<
  TopologyNodeData,
  TopologyEdgeData,
  NodeId,
  EdgeId
>;
export type UnpositionedTopologyGraph = Graph<
  UnpositionedTopologyNode,
  UnpositionedTopologyEdgeData,
  NodeId,
  EdgeId
>;

export type PositionedPathLookupGraph = Graph<
  PathLookupNodeData,
  PathLookupEdgeData,
  NodeId,
  EdgeId
>;
export type UnpositionedPathLookupGraph = Graph<
  UnpositionedPathLookupNode,
  UnpositionedPathLookupEdgeData,
  NodeId,
  EdgeId
>;
